import { defineStore } from 'pinia';
import { client } from '@/apollo';
import * as yup from 'yup';

import type {
  Eatfresh_User_Account,
  Fetch_User_By_Id_Anonymous_QueryQuery,
} from '@/gql/graphql';
import type { Eatfresh_Basic_User_Type_Enum as BasicUserType } from '@/gql/graphql';
import { graphql } from '@/gql';
import { ApiRequest } from '@/stores/shared_store';
import type { Eatfresh_Basic_User_Type_Enum } from '@/gql/graphql';
import { useAuthStore } from '@/stores/auth';
import type { BeforeInstallPromptEvent } from '@/pwa/types';

export type { BeforeInstallPromptEvent };

export enum PasswordCreationTypeFE {
  GENERATE = 'GENERATE',
  SPECIFY = 'SPECIFY',
}

type RegisterAnonymousUserFields = {
  email: string;
  basic_user_type: Eatfresh_Basic_User_Type_Enum;
  has_password?: boolean;
  password?: string;
  agb_accepted: boolean;
  active?: boolean;
};
interface ApiRequestRequestParameters {
  url: string;
  body: {
    id: string;
    newPassword: string;
  };
  options?: {
    useAuthorization?: boolean;
    responseType?: 'string' | 'json';
    headers?: Array<{ name: string; value: string }>;
  };
}
interface CreateEmployeeProperties {
  email: string;
  basic_user_type: BasicUserType;
  agb_accepted: boolean;
}
interface CreateCustomerProperties {
  email: string;
  password: string;
  agb_accepted: boolean;
}

interface UserStoreState {
  currentUser:
    | undefined
    | Fetch_User_By_Id_Anonymous_QueryQuery['eatfresh_user_account'][number];
  pwaInstallation: {
    beforeInstallPromptEvent: null | BeforeInstallPromptEvent;
    newRegister: boolean;
  };
}

export const FETCH_USER_BY_ID_QUERY = graphql(`
  query FETCH_USER_BY_ID_QUERY($user_ID: uuid!) {
    eatfresh_user_account(where: { id: { _eq: $user_ID } }) {
      ...UserFragment
      payment_methods {
        ...PaymentMethodFields
      }
    }
  }
`);
const FETCH_USER_BY_ID_ANONYMOUS_QUERY = graphql(`
  query FETCH_USER_BY_ID_ANONYMOUS_QUERY($user_ID: uuid!) {
    eatfresh_user_account(where: { id: { _eq: $user_ID } }) {
      ...AnonUserFragment
    }
  }
`);

const FETCH_USER_BY_EMAIL_QUERY = graphql(`
  query FETCH_USER_BY_EMAIL_QUERY($email: String!) {
    eatfresh_user_account(where: { email: { _eq: $email } }) {
      ...AnonUserFragment
    }
  }
`);

export const useUserStore = defineStore({
  id: 'user',
  state: (): UserStoreState => ({
    currentUser: undefined,
    pwaInstallation: {
      beforeInstallPromptEvent: null,
      newRegister: false,
    },
  }),
  getters: {},
  actions: {
    //  DELETE user
    async deleteUser({ userId }: { userId: string }) {
      const res = await ApiRequest<{ message: string }>({
        url: '/rest/user/delete',
        body: {
          id: userId,
        },
        options: { responseType: 'json' },
      });
      return res;
    },
    //  POST user
    async createCustomer(userProperties: CreateCustomerProperties) {
      const response = await ApiRequest<{
        id: string;
      }>({
        url: '/rest/user/create-user-account-by-register',
        body: {
          ...userProperties,
        },
        options: { responseType: 'json' },
      });
      return response;
    },
    async createEmployee(employeeProperties: CreateEmployeeProperties) {
      return await ApiRequest<{
        id: string;
      }>({
        url: '/rest/user/create-user-account-by-staff',
        body: {
          ...employeeProperties, // Provide a default empty string value for the password
        },
        options: { responseType: 'json' },
      });
    },
    async fetchUserById(id: string) {
      const userQueryResult = await client().query({
        query: FETCH_USER_BY_ID_QUERY,
        variables: { user_ID: id },
      });
      const fetchedUser = userQueryResult.data.eatfresh_user_account[0];

      // return fetchedUser
      this.currentUser = fetchedUser;
      return fetchedUser;
    },
    async fetchUserByIdAnonymous(id: string) {
      const userQueryResult = await client().query({
        query: FETCH_USER_BY_ID_ANONYMOUS_QUERY,
        variables: { user_ID: id },
      });
      const fetchedUser = userQueryResult.data.eatfresh_user_account[0];

      this.currentUser = fetchedUser;
      return fetchedUser;
    },
    async fetchUserByEmail(email: string) {
      const lowerCaseEmail = email.toLowerCase();
      const userQueryResult = await client().query({
        query: FETCH_USER_BY_EMAIL_QUERY,
        variables: { email: lowerCaseEmail },
      });
      console.log(userQueryResult);
      const fetchedUser = userQueryResult.data.eatfresh_user_account.at(0);

      // return fetchedUser
      this.currentUser = fetchedUser;
      return fetchedUser;
    },
    async updateUserPassword({
      userId,
      newPassword,
      authToken,
    }: {
      userId: string;
      newPassword: string;
      authToken?: string;
    }) {
      // login as admin
      const requestParameters: ApiRequestRequestParameters = {
        url: '/rest/user/update-password',
        body: {
          id: userId,
          newPassword,
        },
      };
      if (authToken) {
        requestParameters['options'] = {
          headers: [{ name: 'Authorization', value: `Bearer ${authToken}` }],
        };
      }
      return await ApiRequest<string>(requestParameters);
    },
    async updateUser({
      userId,
      updatedUserProperties,
    }: {
      userId: string;
      updatedUserProperties: UpdateUserRequest;
    }) {
      return await ApiRequest<Omit<Eatfresh_User_Account, 'password'>>({
        url: '/rest/user/update',
        body: {
          id: userId,
          ...updatedUserProperties,
        },
        options: { responseType: 'json' },
      });
    },
    async createAnonymousUser() {
      return ApiRequest<{ database: { id: string } }>({
        url: '/rest/user/create-anonymous',
        options: { responseType: 'json' },
      });
    },
    async sendAccountActivationEmail(emailAddress: string) {
      return ApiRequest({
        url: '/rest/email/send-account-activation-email',
        body: {
          emailAddress,
        },
        options: { responseType: 'json' },
      });
    },
    async registerAnonymousUser({
      userId,
      registerProperties,
    }: {
      userId: string;
      registerProperties: RegisterAnonymousUserFields;
    }) {
      return await this.updateUser({
        userId,
        updatedUserProperties: registerProperties,
      });
    },
    async acceptTermsAndConditions() {
      const authStore = useAuthStore();
      const acceptTermsHeaders = new Headers();

      if (!authStore.id) {
        throw new Error('No user id provided');
      }
      acceptTermsHeaders.append('Content-Type', 'application/json');
      acceptTermsHeaders.append(
        'Authorization',
        'Bearer ' + authStore.authToken,
      );

      const acceptTermsBody = {
        id: authStore.id,
      };

      const acceptTermsOptions = {
        method: 'POST',
        headers: acceptTermsHeaders,
        body: JSON.stringify(acceptTermsBody),
      };

      const response = await fetch(
        '/rest/user/accept-terms',
        acceptTermsOptions,
      );

      return response;
    },
  },
});

export const updateUserSchema = yup.object({
  email: yup.string().required().email(),
  first_name: yup.string(),
  last_name: yup.string(),
  agb_accepted: yup.boolean(),
  password: yup.string(),
  has_password: yup.boolean(),
  biometrics: yup.boolean(),
});

export type UpdateUserRequest = yup.InferType<typeof updateUserSchema>;

export const addEmailToAnonymousUserSchema = yup.object({
  id: yup.string().required().uuid(),
  email: yup.string().required().email(),
  contact_accepted: yup
    .boolean()
    .isTrue('You must accept the terms to continue.')
    .required(),
  agb_accepted: yup
    .boolean()
    .isTrue('You must accept the terms to continue.')
    .required(),
});

export type AddEmailToAnonymousUserRequest = yup.InferType<
  typeof addEmailToAnonymousUserSchema
>;

export function addEmailToAnonymousUser(data: AddEmailToAnonymousUserRequest) {
  return ApiRequest<{ message: string }>({
    url: '/rest/user/add-email-to-anonymous',
    body: data,
    options: { responseType: 'json' },
  });
}
